import { Component, ReactNode } from 'react';

import { isAxiosError } from '@mint-lib/api';
import { trackAction, trackError } from '@mint-lib/datadog';

import { ErrorPlaceholder } from './ErrorPlaceholder.jsx';
import { ForbiddenErrorPage } from './pages/ForbiddenErrorPage.jsx';
import { InternalServerErrorPage } from './pages/InternalServerErrorPage.jsx';

const FORBIDDEN_STATUS = 403;
const INTERNAL_SERVER_ERROR_STATUS = 500;

function getErrorStatus(error: unknown): number | undefined {
  if (!isAxiosError(error)) {
    return undefined;
  }
  return error.status ?? error.response?.status;
}

type State = {
  hasError: boolean;
  error?: Error;
  prevScope: string;
};

type Props = {
  children: ReactNode;
  fallback?: ReactNode;
  onReset?: () => void;
  type?: 'component' | 'page';
  isGuest?: boolean;
  scope?: string;
  hasForbiddenOn403?: boolean;
};

/**
 * ErrorBoundary is a React Component that catches JavaScript errors anywhere in their child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 * It also sends these errors to Datadog Real User Monitoring (RUM) for further analysis.
 *
 * @component
 *
 * @param children - The child components that the ErrorBoundary will encapsulate.
 * @param onReset - A function that will be called when resetting the error state.
 * @param type - The type of the ErrorBoundary, can be either 'component' or 'page'.
 * @param scope - The scope in which the ErrorBoundary is used, default is 'error-boundary'.
 *
 * @example
 * <ErrorBoundary type="page" scope="homepage">
 *   <Homepage />
 * </ErrorBoundary>
 */
export class ErrorBoundary extends Component<Props, State> {
  static defaultProps = {
    scope: 'error-boundary-top-level',
  };

  constructor(props: any) {
    super(props);
    this.state = { hasError: false, prevScope: props.scope };
  }

  static getDerivedStateFromError(error: any) {
    return { hasError: true, error };
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    if (state.prevScope !== props.scope) {
      return {
        hasError: false,
        error: undefined,
        prevScope: props.scope,
      };
    }

    return state;
  }

  componentDidCatch(error: Error) {
    console.error('errorboundary', error);
    // TODO: remove after separate deployment release
    if (
      error.message?.includes('Failed to fetch dynamically imported module')
    ) {
      trackAction('Reload the page', {
        /**
         * Action frustration properties
         */
        frustration: {
          /**
           * Action frustration types
           */
          type: 'dead_click',
        },

        purpose: 'We reloaded the page due to new version of the app',
      });
      globalThis.location.reload();
      return;
    }

    trackError(error?.message ?? error, {
      scope: this.props.scope,
      type: 'error-boundary',
    });
  }

  retry = () => {
    this.props.onReset?.();
    this.setState({ hasError: false });
  };

  render() {
    // an empty div to not bother users with an error page
    // TODO: remove after separate deployment release
    if (
      this.state.error?.message?.includes(
        'Failed to fetch dynamically imported module',
      )
    ) {
      return <div />;
    }
    if (this.state.hasError && this.props.fallback) {
      return this.props.fallback;
    }
    if (this.state.hasError) {
      if (this.props.type === 'page') {
        const status = getErrorStatus(this.state.error);
        if (this.props.hasForbiddenOn403 && status === FORBIDDEN_STATUS) {
          return <ForbiddenErrorPage />;
        }
        if (status === INTERNAL_SERVER_ERROR_STATUS) {
          return <InternalServerErrorPage />;
        }
      }
      return <ErrorPlaceholder retry={this.retry} />;
    }

    return this.props.children;
  }
}
